import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { X, Cookie, ShieldCheck, BarChart3 } from 'lucide-react';

const CookieBanner = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setIsOpen(true), 1800);
      return () => clearTimeout(timer);
    }
  }, []);

  const closeBanner = () => {
    setIsOpen(false);
  };

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    localStorage.setItem('cookie_analytics', 'true');
    closeBanner();
    setShowCustomize(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookie_consent', 'rejected');
    localStorage.setItem('cookie_analytics', 'false');
    closeBanner();
    setShowCustomize(false);
  };

  const handleSave = () => {
    localStorage.setItem('cookie_consent', 'customized');
    localStorage.setItem('cookie_analytics', String(analyticsEnabled));
    closeBanner();
    setShowCustomize(false);
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && !showCustomize && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22, mass: 0.6 }}
            className="fixed bottom-24 lg:bottom-6 left-4 right-4 lg:left-auto lg:right-6 lg:max-w-md z-50"
            data-testid="cookie-banner"
          >
            <div className="relative overflow-hidden bg-[#0B0B0C]/95 backdrop-blur-xl border border-white/10 rounded-2xl p-5 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]">
              {/* Decorative gold gradient top line */}
              <div
                aria-hidden="true"
                className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent"
              />

              {/* Decorative blur orb */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#C9A84C]/10 blur-3xl"
              />

              <div className="relative flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-[#C9A84C]" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-base text-[#F5F0E8] mb-1 uppercase tracking-wider">
                    Cookies
                  </h3>
                  <p className="text-sm text-[#B9B2A8] leading-relaxed">
                    {t('cookies.message')}
                  </p>
                </div>
                <button
                  onClick={closeBanner}
                  className="flex-shrink-0 text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors p-1 -mt-1 -mr-1"
                  aria-label="Fermer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="relative flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={handleAccept}
                  className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A] text-xs uppercase tracking-wider font-semibold flex-1 min-w-[90px]"
                  data-testid="cookie-accept-button"
                >
                  {t('cookies.accept')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReject}
                  className="border-white/20 text-[#F5F0E8] hover:bg-white/5 text-xs uppercase tracking-wider flex-1 min-w-[90px]"
                  data-testid="cookie-reject-button"
                >
                  {t('cookies.reject')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowCustomize(true)}
                  className="text-[#B9B2A8] hover:text-[#F5F0E8] hover:bg-white/5 text-xs uppercase tracking-wider"
                  data-testid="cookie-customize-button"
                >
                  {t('cookies.customize')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Sheet open={showCustomize} onOpenChange={setShowCustomize}>
        <SheetContent
          side="bottom"
          className="bg-[#0B0B0C] border-t border-white/10 max-h-[80vh]"
        >
          <SheetHeader>
            <SheetTitle className="text-[#F5F0E8] font-display uppercase tracking-wider text-xl">
              Paramètres des cookies
            </SheetTitle>
          </SheetHeader>
          <div className="py-6 space-y-3 max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-[#F5F0E8] font-medium">Cookies essentiels</p>
                  <p className="text-xs text-[#B9B2A8]">Nécessaires au fonctionnement du site</p>
                </div>
              </div>
              <span className="text-xs text-emerald-400 uppercase tracking-wider font-semibold flex-shrink-0">
                Toujours actifs
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18 }}
              className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-xl"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-[#C9A84C]" />
                </div>
                <div>
                  <p className="text-sm text-[#F5F0E8] font-medium">Cookies analytiques</p>
                  <p className="text-xs text-[#B9B2A8]">Pour améliorer notre site</p>
                </div>
              </div>
              <button
                onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                role="switch"
                aria-checked={analyticsEnabled}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                  analyticsEnabled ? 'bg-[#C9A84C]' : 'bg-white/10'
                }`}
              >
                <motion.span
                  animate={{ x: analyticsEnabled ? 24 : 2 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                  className="absolute top-1 left-0 w-4 h-4 rounded-full bg-white shadow-lg"
                />
              </button>
            </motion.div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleReject}
                className="flex-1 border-white/20 text-[#F5F0E8] hover:bg-white/5 uppercase tracking-wider text-xs"
              >
                {t('cookies.reject')}
              </Button>
              <Button
                onClick={handleSave}
                className="flex-1 bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A] uppercase tracking-wider text-xs font-semibold"
              >
                Sauvegarder
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default CookieBanner;
